import { create } from 'zustand'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastItem {
  id: string
  type: ToastType
  message: string
  duration?: number
}

interface ToastState {
  toasts: ToastItem[]
  addToast: (toast: Omit<ToastItem, 'id'>) => void
  removeToast: (id: string) => void
}

const MAX_TOASTS = 5

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (toast) =>
    set((state) => {
      const id = Math.random().toString(36).substring(2, 9)
      const newToast = { ...toast, id }
      const newToasts = [newToast, ...state.toasts].slice(0, MAX_TOASTS)
      return { toasts: newToasts }
    }),
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
}))

export const toast = {
  success: (message: string, options?: { duration?: number }) => {
    useToastStore.getState().addToast({ type: 'success', message, duration: options?.duration })
  },
  error: (message: string, options?: { duration?: number }) => {
    useToastStore.getState().addToast({ type: 'error', message, duration: options?.duration })
  },
  info: (message: string, options?: { duration?: number }) => {
    useToastStore.getState().addToast({ type: 'info', message, duration: options?.duration })
  },
  warning: (message: string, options?: { duration?: number }) => {
    useToastStore.getState().addToast({ type: 'warning', message, duration: options?.duration })
  },
  dismiss: (id: string) => {
    useToastStore.getState().removeToast(id)
  }
}

export function useToast() {
  const toasts = useToastStore((state) => state.toasts)
  const removeToast = useToastStore((state) => state.removeToast)

  return { toasts, removeToast }
}
